import React from 'react';
import { formatCurrency } from '../../utils/formatters';
import StatusBadge from './StatusBadge';

// Icons
const CloseIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const UserIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const MapPinIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const PlaneIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CreditCardIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const BookingsIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
);

const ServiceIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const BookingDetailCard = ({ booking, isOpen, onClose, isDarkMode = false }) => {
  if (!isOpen || !booking) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'green';
      case 'pending':
        return 'yellow';
      case 'failed':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getServiceDetails = (booking) => {
    // Use serviceDetails from booking if available, otherwise fallback to serviceType
    const serviceType = booking.serviceDetails?.categoryId || booking.serviceType?.toLowerCase();
    const levelName = booking.serviceDetails?.levelName;
    
    const serviceDetails = {
      'arrival': {
        title: 'Arrival Meet & Greet',
        description: 'Personal assistance upon arrival at the airport',
        features: [
          'Meet at arrival gate or baggage claim',
          'Assist with luggage handling',
          'Escort to ground transportation',
          'Provide local information and assistance'
        ],
        duration: '30-45 minutes',
        icon: '✈️'
      },
      'departure': {
        title: 'Departure Meet & Greet',
        description: 'Personal assistance for departure from the airport',
        features: [
          'Meet at check-in area or terminal entrance',
          'Assist with check-in and baggage drop',
          'Escort through security and immigration',
          'Guide to departure gate'
        ],
        duration: '45-60 minutes',
        icon: '🛫'
      },
      'transit': {
        title: 'Transit Meet & Greet',
        description: 'Assistance during airport transit connections',
        features: [
          'Meet at arrival gate',
          'Assist with terminal transfer',
          'Help with re-check-in if required',
          'Escort to departure gate'
        ],
        duration: '60-90 minutes',
        icon: '🔄'
      },
      'arrival_and_departure': {
        title: 'Arrival & Departure Package',
        description: 'Complete airport assistance for both arrival and departure',
        features: [
          'Arrival meet & greet service',
          'Departure meet & greet service',
          'Priority assistance throughout',
          'Dedicated concierge support'
        ],
        duration: '2-3 hours total',
        icon: '🎯'
      }
    };
    
    const baseService = serviceDetails[serviceType] || {
      title: serviceType,
      description: 'Airport meet & greet service',
      features: ['Personal assistance', 'Luggage handling', 'Terminal guidance'],
      duration: '30-60 minutes',
      icon: '✈️'
    };

    // Add level information if available
    if (levelName) {
      baseService.title = `${baseService.title} - ${levelName}`;
    }

    return baseService;
  };

  const serviceInfo = getServiceDetails(booking);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className={`rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto transition-colors duration-300 ${
        isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
      }`}>
        {/* Modal Header */}
        <div className={`flex items-center justify-between p-6 border-b transition-colors duration-300 ${
          isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
        }`}>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center">
              <BookingsIcon />
            </div>
            <div>
              <h2 className={`text-xl font-bold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Booking Details</h2>
              <p className={`text-sm transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>Service: {serviceInfo.title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              isDarkMode 
                ? 'hover:bg-skyone-inset-dark text-gray-300 hover:text-white' 
                : 'hover:bg-gray-100 text-gray-600 hover:text-gray-800'
            }`}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-6">
          {/* Service Information */}
          <div className={`rounded-xl p-6 transition-colors duration-300 ${
            isDarkMode 
              ? 'bg-gradient-to-r from-blue-900 to-indigo-900' 
              : 'bg-gradient-to-r from-blue-50 to-indigo-50'
          }`}>
            <div className="flex items-start space-x-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl transition-colors duration-300 ${
                isDarkMode ? 'bg-blue-800' : 'bg-blue-100'
              }`}>
                {serviceInfo.icon}
              </div>
              <div className="flex-1">
                <h3 className={`text-lg font-bold mb-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>{serviceInfo.title}</h3>
                <p className={`mb-3 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>{serviceInfo.description}</p>
                <div className={`flex items-center space-x-4 text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <div className="flex items-center space-x-1">
                    <ClockIcon />
                    <span>Duration: {serviceInfo.duration}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Service Features */}
          <div className="space-y-4">
            <h3 className={`text-lg font-semibold flex items-center space-x-2 transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <ServiceIcon />
              <span>Service Features</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {serviceInfo.features.map((feature, index) => (
                <div key={index} className={`flex items-center space-x-3 p-3 rounded-lg transition-colors duration-300 ${
                  isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
                }`}>
                  <CheckIcon />
                  <span className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Status and Amount */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className={`rounded-lg p-4 transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
            }`}>
              <div className="flex items-center space-x-3 mb-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-300 ${
                  isDarkMode ? 'bg-blue-800' : 'bg-blue-100'
                }`}>
                  <span className={`text-sm font-semibold transition-colors duration-300 ${
                    isDarkMode ? 'text-blue-300' : 'text-blue-600'
                  }`}>£</span>
                </div>
                <div>
                  <p className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Amount</p>
                  <p className={`text-lg font-bold transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {formatCurrency(booking.amount, booking.currency || 'GBP')}
                  </p>
                </div>
              </div>
            </div>
            <div className={`rounded-lg p-4 transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
            }`}>
              <div className="flex items-center space-x-3 mb-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-300 ${
                  isDarkMode ? 'bg-green-800' : 'bg-green-100'
                }`}>
                  <CheckIcon />
                </div>
                <div>
                  <p className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Status</p>
                  <StatusBadge 
                    status={booking.status} 
                    color={getStatusColor(booking.status)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Booking Information */}
          <div className="space-y-4">
            <h3 className={`text-lg font-semibold flex items-center space-x-2 transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <PlaneIcon />
              <span>Booking Information</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`flex items-center space-x-3 p-3 rounded-lg transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
              }`}>
                <CalendarIcon />
                <div>
                  <p className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Booking Date</p>
                  <p className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {new Date(booking.createdAt).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>

              <div className={`flex items-center space-x-3 p-3 rounded-lg transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
              }`}>
                <CalendarIcon />
                <div>
                  <p className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Service Date</p>
                  <p className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {booking.serviceDate ? 
                      new Date(booking.serviceDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : 
                      'Not specified'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Passenger Information */}
          {(booking.passengerName || booking.passengerEmail || booking.passengerPhone) && (
            <div className="space-y-4">
              <h3 className={`text-lg font-semibold flex items-center space-x-2 transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                <UserIcon />
                <span>Passenger Information</span>
              </h3>
              
              <div className="space-y-3">
                {booking.passengerName && (
                  <div className={`flex items-center space-x-3 p-3 rounded-lg transition-colors duration-300 ${
                    isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
                  }`}>
                    <UserIcon />
                    <div>
                      <p className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>Name</p>
                      <p className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>{booking.passengerName}</p>
                    </div>
                  </div>
                )}

                {booking.passengerEmail && (
                  <div className={`flex items-center space-x-3 p-3 rounded-lg transition-colors duration-300 ${
                    isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
                  }`}>
                    <UserIcon />
                    <div>
                      <p className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>Email</p>
                      <p className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>{booking.passengerEmail}</p>
                    </div>
                  </div>
                )}

                {booking.passengerPhone && (
                  <div className={`flex items-center space-x-3 p-3 rounded-lg transition-colors duration-300 ${
                    isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
                  }`}>
                    <PhoneIcon />
                    <div>
                      <p className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>Phone</p>
                      <p className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>{booking.passengerPhone}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Flight Information */}
          {(booking.flightNumber || booking.airline || booking.terminal) && (
            <div className="space-y-4">
              <h3 className={`text-lg font-semibold flex items-center space-x-2 transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                <PlaneIcon />
                <span>Flight Information</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {booking.flightNumber && (
                  <div className={`flex items-center space-x-3 p-3 rounded-lg transition-colors duration-300 ${
                    isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
                  }`}>
                    <PlaneIcon />
                    <div>
                      <p className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>Flight Number</p>
                      <p className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>{booking.flightNumber}</p>
                    </div>
                  </div>
                )}

                {booking.airline && (
                  <div className={`flex items-center space-x-3 p-3 rounded-lg transition-colors duration-300 ${
                    isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
                  }`}>
                    <PlaneIcon />
                    <div>
                      <p className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>Airline</p>
                      <p className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>{booking.airline}</p>
                    </div>
                  </div>
                )}

                {booking.terminal && (
                  <div className={`flex items-center space-x-3 p-3 rounded-lg transition-colors duration-300 ${
                    isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
                  }`}>
                    <MapPinIcon />
                    <div>
                      <p className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>Terminal</p>
                      <p className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>{booking.terminal}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Notes */}
          {booking.notes && (
            <div className="space-y-4">
              <h3 className={`text-lg font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Additional Notes</h3>
              <div className={`p-4 rounded-lg transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
              }`}>
                <p className={`text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>{booking.notes}</p>
              </div>
            </div>
          )}


          {/* Actions */}
          <div className={`flex items-center justify-end space-x-4 pt-4 border-t transition-colors duration-300 ${
            isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
          }`}>
            <button
              onClick={onClose}
              className={`px-6 py-2 font-medium transition-colors duration-200 ${
                isDarkMode 
                  ? 'text-gray-300 hover:text-white' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Close
            </button>
            {booking.status === 'pending' && booking.paymentLink && (
              <a
                href={booking.paymentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2"
              >
                <CreditCardIcon />
                <span>Pay Now</span>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetailCard;
